// Valuation freshness helpers for illiquid, appraisal-carried holdings.
// Market-priced classes refresh daily from a price feed; every other class is carried
// at its most recent manual appraisal. Surfacing that appraisal's date — and flagging
// stale ones — keeps an old valuation from silently inflating net worth.

import { isMarketPricedClass } from '@steward-app/steward-types';

// There is deliberately no exported SET of market-priced classes here any more. It was
// one of seven independent lists of "which classes do I care about", and a raw set is
// the wrong shape: it can only be matched by exact string, so it answers differently
// from `isMarketPriced` for a stored 'Equity' or 'stock'. Ask the registry instead.

/** An appraisal older than this is shown as "stale". */
export const STALE_VALUATION_DAYS = 365;

/** AVM refresh triggers when the last valuation is older than this. */
export const AVM_STALE_DAYS = 30;

export function isMarketPriced(assetClass: string): boolean {
  return isMarketPricedClass(assetClass);
}

/**
 * The date to caption a holding's displayed value with, or undefined when the value speaks
 * for itself (a market-priced holding valued from a price, which carries its own "official
 * close, <date>" line).
 *
 * One helper because there are THREE renderers of this caption — the asset page, the assets
 * list and the portfolio table — and #392 originally taught only one of them that a
 * market-priced holding can now be valued from a provider valuation instead of a price. The
 * same holding then showed a dated value in one view and an undated one in the others.
 *
 * `priceAsOf` present is the discriminator: `enrichAssets` sets it only when a price was
 * actually resolved, so its absence alongside a `currentValue` means the value came from a
 * valuation and the reader deserves its date.
 */
export function valueAsOf(attrs: {
  assetClass: string;
  currentValueAsOf?: string;
  priceAsOf?: string;
}): string | undefined {
  if (isMarketPriced(attrs.assetClass) && attrs.priceAsOf) return undefined;
  return attrs.currentValueAsOf;
}

/**
 * The provider's price ONLY when it can be shown as a per-unit money amount without
 * guessing — otherwise undefined.
 *
 * `providerPrice` is carried verbatim in the provider's own units, and those units are not
 * uniform: dollars per unit for equities and funds, percent of par for a bond (#393). So
 * rendering it as "$150.63" would be a fabrication for exactly the holdings this display
 * exists to make legible.
 *
 * The test is the same consistency check the mapper uses, run against numbers already on
 * screen: if `quantity x providerPrice` reproduces the displayed value, the price is
 * unambiguously money per unit and can be labelled as such. If it does not, we know the
 * units are something else and we say nothing rather than something wrong. The tolerance
 * is the rounding error implied by a price quoted to the cent.
 *
 * ⚠️ **Do not add the option contract multiplier here.** The mapper's rule 3 recognises a
 * holding whose value is exactly 100x `quantity x price` — Fidelity reporting an option's
 * quantity in contracts (#570) — and it is tempting to mirror that here so the price can be
 * shown with a "100 shares per contract" note. The arithmetic does not carry that claim. The
 * same 100x reads equally as an institution reporting the VALUE in cents against a price and
 * quantity in dollars, and there the position is worth a hundredth of what it says; a label
 * naming contracts would be a statement about the instrument, made from a number that does
 * not identify one.
 *
 * The mapper dares to act on the shape for two reasons this function has neither of. It
 * changes no number when it is wrong (it returns the provider's value either way, so a false
 * positive costs a suppressed warning), and it fences the rule on the provider's own security
 * type. What reaches the client is `assetClass`, a lossy bucket: Plaid's `derivative` becomes
 * `alternative` and its `other` stays `other`, so the fence cannot be reproduced here even in
 * principle. Saying nothing remains the honest answer until a real per-instrument signal is
 * carried through, which Plaid does not give us for this institution anyway.
 */
export function providerUnitPrice(attrs: {
  providerPrice?: number;
  currentQuantity?: number;
  currentValue?: number;
}): number | undefined {
  const { providerPrice, currentQuantity, currentValue } = attrs;
  if (providerPrice === undefined || currentQuantity === undefined || currentValue === undefined) {
    return undefined;
  }
  // Quantity 0 makes the test vacuous: `0 x anything - 0` is 0, so EVERY price would pass
  // and a percent-of-par bond quote would render as "$150.63" beside "Quantity 0" — the
  // precise fabrication this function exists to prevent, reached from the other end. A
  // closed position (Plaid still reports it until `reconcile` drops the row) proves nothing
  // about the units, so it gets no price.
  if (currentQuantity === 0) return undefined;
  // The ULP term matches the mapper's `roundingTolerance` and is here for the same reason:
  // half a cent per unit is exact in decimal but the comparison runs in binary, so a price
  // rounded by EXACTLY half a cent lands on the bound and is decided by a few ULPs. Prod hit
  // that tie on a real holding (5,782 units, price 14.07, value 81,323.83 — #499). Here the
  // symptom is silence rather than a warning: the units ARE money per unit, and the family
  // is shown no price at all on a holding we could have labelled.
  const product = currentQuantity * providerPrice;
  // A non-finite product contributes NO slack: it would make the tolerance Infinity, and
  // `Infinity <= Infinity` would label an overflowing product as money per unit — printing a
  // fabricated price, the exact thing this function exists to prevent.
  const slack = Number.isFinite(product) ? Math.abs(product) * 8 * Number.EPSILON : 0;
  const tolerance = Math.max(0.01, Math.abs(currentQuantity) * 0.005) + slack;
  return Math.abs(product - currentValue) <= tolerance ? providerPrice : undefined;
}

export function isStaleValuation(asOf: string | undefined): boolean {
  if (!asOf) return false;
  const then = new Date(`${asOf}T00:00:00Z`).getTime();
  if (Number.isNaN(then)) return false;
  return Date.now() - then > STALE_VALUATION_DAYS * 24 * 60 * 60 * 1000;
}

/** Returns the number of days since `asOf` (YYYY-MM-DD), or null if unparseable. */
export function daysSince(asOf: string | undefined): number | null {
  if (!asOf) return null;
  const then = new Date(`${asOf}T00:00:00Z`).getTime();
  if (Number.isNaN(then)) return null;
  return Math.floor((Date.now() - then) / (24 * 60 * 60 * 1000));
}

/** Returns true if the AVM value is older than AVM_STALE_DAYS. */
export function isAvmStale(asOf: string | undefined): boolean {
  const days = daysSince(asOf);
  return days === null || days > AVM_STALE_DAYS;
}

/**
 * Renders an ISO date (YYYY-MM-DD) as e.g. "Jun 14, 2026"; passes through unparseable input.
 *
 * A named re-export, not a second implementation. The timezone rule that keeps a
 * date-only value on the same calendar date for every viewer used to be written out
 * twice — here, correctly, and in `fmtDate` where it was missing — so the asset page
 * printed a valuation date right and a lot's effective date a day early. It now lives
 * once, in `lib/formatDate.ts`.
 */
export { fmtDate as fmtValuationDate } from './formatDate';
